import { useCallback } from "react";
import { useSudoContext } from "../../SudoContext";

// establishSudo rejects with this message when the user backs out of the flow.
const SUDO_CANCELLED_MESSAGE = "Sudo flow was cancelled";

/** True when the error signals the user dismissed the MFA/sudo verification. */
export const isSudoCancelled = (error: unknown): boolean =>
  error instanceof Error && error.message === SUDO_CANCELLED_MESSAGE;

/**
 * True when the API response is a sudo/MFA challenge. `ApiHttp` surfaces these
 * by `id`, so we match on that rather than the HTTP status: the token endpoints
 * are session-only, which makes DRF return 403 (not the 401 that token-authed
 * endpoints yield) for the same `sudo_authentication_failed` error.
 */
const isSudoRequired = (error: unknown): boolean =>
  !!error &&
  typeof error === "object" &&
  "id" in error &&
  (error as { id?: unknown }).id === "sudo_authentication_failed";

/**
 * Wraps a token write so that a `sudo_authentication_failed` response triggers
 * the shared MFA/sudo verification modal, then retries the write once sudo is
 * established.
 *
 * Args:
 *     returnPath: Router-relative path to return to after verification (the
 *         current write pane), keeping it mounted beneath the modal.
 *
 * Returns:
 *     A runner that executes `write`, prompting for MFA only when required.
 */
export default function useSudoWrite(returnPath: string) {
  const { establishSudo } = useSudoContext();
  return useCallback(
    async <T>(write: () => Promise<T>): Promise<T> => {
      try {
        return await write();
      } catch (error) {
        if (!isSudoRequired(error)) throw error;
        await establishSudo(returnPath);
        return write();
      }
    },
    [establishSudo, returnPath],
  );
}

